import { useEffect, useState } from 'react';
import { getCenterConfig } from './db';

type TranslationKey = keyof typeof translations.es;

const translations = {
  es: {
    // PDF labels
    pdf_academicYear: 'Curso Académico',
    pdf_date: 'Fecha',
    pdf_student: 'Estudiante',
    pdf_course: 'Curso',
    pdf_instrument: 'Instrumento',
    pdf_notAssigned: 'No asignado',
    pdf_attendance: 'FALTAS',
    pdf_globalGrade: 'CALIFICACIÓN',
    pdf_global: 'GLOBAL',
    pdf_observations: 'Observaciones',
    pdf_teacherSignature: 'Firma del Profesor/a',
    pdf_directorSignature: 'Firma del Director/a',
    pdf_page: 'Página',
    pdf_of: 'de',
    // UI labels  
    ui_students: 'Alumnos',
    ui_teachers: 'Profesores',
    ui_courses: 'Cursos',
    ui_reportCards: 'Boletines',
    ui_grades: 'Introducción de Notas',
    ui_subjects: 'Reparto de asignaturas',
    ui_settings: 'Configuración',
    ui_help: 'Ayuda',
    ui_home: 'Inicio',
    ui_group_organization: 'Organización',
    ui_group_evaluation: 'Evaluación',
    ui_group_system: 'Sistema',
    ui_statistics: 'Estadísticas',
    ui_logout: 'Cerrar Sesión',
    ui_save: 'Guardar',
    ui_cancel: 'Cancelar',
    ui_delete: 'Eliminar',
    ui_edit: 'Editar',
    ui_add: 'Añadir',
    ui_loading: 'Cargando...',
    ui_noData: 'Sin datos',
    ui_search: 'Buscar',
    ui_name: 'Nombre',
    ui_nameNotAvailable: 'Nombre no disponible',
  },
  ca: {
    pdf_academicYear: 'Curs Acadèmic',
    pdf_date: 'Data',
    pdf_student: 'Estudiant',
    pdf_course: 'Curs',
    pdf_instrument: 'Instrument',
    pdf_notAssigned: 'No assignat',
    pdf_attendance: 'FALTES',
    pdf_globalGrade: 'QUALIFICACIÓ',
    pdf_global: 'GLOBAL',
    pdf_observations: 'Observacions',
    pdf_teacherSignature: 'Signatura del Professor/a',
    pdf_directorSignature: 'Signatura del Director/a',
    pdf_page: 'Pàgina',
    pdf_of: 'de',
    ui_students: 'Alumnes',
    ui_teachers: 'Professors',
    ui_courses: 'Cursos',
    ui_reportCards: 'Butlletins',
    ui_grades: 'Introducció de Notes',
    ui_subjects: 'Repartiment docent',
    ui_settings: 'Configuració',
    ui_help: 'Ajuda',
    ui_home: 'Inici',
    ui_group_organization: 'Organització',
    ui_group_evaluation: 'Avaluació',
    ui_group_system: 'Sistema',
    ui_statistics: 'Estadístiques',
    ui_logout: 'Tancar Sessió',
    ui_save: 'Desar',
    ui_cancel: 'Cancel·lar',
    ui_delete: 'Eliminar',
    ui_edit: 'Editar',
    ui_add: 'Afegir',
    ui_loading: 'Carregant...',
    ui_noData: 'Sense dades',
    ui_search: 'Cercar',
    ui_name: 'Nom',
    ui_nameNotAvailable: 'Nom no disponible',
  },
  va: {
    pdf_academicYear: 'Curs Acadèmic',
    pdf_date: 'Data',
    pdf_student: 'Estudiant',
    pdf_course: 'Curs',
    pdf_instrument: 'Instrument',
    pdf_notAssigned: 'No assignat',
    pdf_attendance: 'FALTES',
    pdf_globalGrade: 'QUALIFICACIÓ',
    pdf_global: 'GLOBAL',
    pdf_observations: 'Observacions',
    pdf_teacherSignature: 'Signatura del Professor/a',
    pdf_directorSignature: 'Signatura del Director/a',
    pdf_page: 'Pàgina',
    pdf_of: 'de',
    ui_students: 'Alumnes',
    ui_teachers: 'Professors',
    ui_courses: 'Cursos',
    ui_reportCards: 'Butlletins',
    ui_grades: 'Introducció de Notes',
    ui_subjects: 'Repartiment docent',
    ui_settings: 'Configuració',
    ui_help: 'Ajuda',
    ui_home: 'Inici',
    ui_group_organization: 'Organització',
    ui_group_evaluation: 'Avaluació',
    ui_group_system: 'Sistema',
    ui_statistics: 'Estadístiques',
    ui_logout: 'Tancar Sessió',
    ui_save: 'Guardar',
    ui_cancel: 'Cancel·lar',
    ui_delete: 'Eliminar',
    ui_edit: 'Editar',
    ui_add: 'Afegir',
    ui_loading: 'Carregant...',
    ui_noData: 'Sense dades',
    ui_search: 'Cercar',
    ui_name: 'Nom',
    ui_nameNotAvailable: 'Nom no disponible',
  },
} as const;

type Language = keyof typeof translations;

let currentLanguage: Language = 'es';
const listeners = new Set<() => void>();

export function setLanguage(lang: Language) {
  currentLanguage = lang;
  listeners.forEach(fn => fn());
}

export function t(key: TranslationKey): string {
  return translations[currentLanguage]?.[key] ?? translations.es[key] ?? key;
}

export function tForLang(lang: string, key: TranslationKey): string {
  const l = (lang || 'es') as Language;
  return translations[l]?.[key] ?? translations.es[key] ?? key;
}

export function useI18n() {
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    const listener = () => forceUpdate(n => n + 1);
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  }, []);

  return { t, currentLanguage };
}

// Initialize language from center config
export async function initLanguage() {
  try {
    const config = await getCenterConfig();
    if (config?.language) {
      setLanguage(config.language as Language);
    }
  } catch {
    // ignore
  }
}
